/** One statement: lines, PDF, and the payments recorded against it (§4.6). */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { Ban, Check, Download, Eye, Plus, X } from 'lucide-react';
import { api, ApiError, getAccessToken } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { peso, shortDate } from '../../lib/format';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  Modal,
  Skeleton,
  StatusChip,
  TableWrap,
  Td,
  Th,
  inputClass,
} from '../../components/ui';
import { BILLING_LABELS, BILLING_TONES } from './BillingPage';

interface Collection {
  id: string;
  dateReceived: string;
  cashAmount: string;
  ewtAmount: string;
  wvatAmount: string;
  form2307Received: boolean;
  form2306Received: boolean;
  referenceNo: string | null;
  orInvoiceNo: string | null;
  remarks: string | null;
  status: 'PENDING' | 'CONFIRMED' | 'REJECTED';
  paymentMode: { label: string } | null;
}

interface BillingDetail {
  id: string;
  code: string;
  type: string;
  billingPeriod: string | null;
  billingDate: string;
  dueDate: string;
  amountVatEx: string;
  vat: string;
  total: string;
  settledAmount: string;
  status: keyof typeof BILLING_LABELS;
  remarks: string | null;
  voidReason: string | null;
  lines: { id: string; description: string; amountVatEx: string }[];
  collections: Collection[];
  salesOrder: {
    id: string;
    code: string;
    projectName: string;
    isGovernment: boolean;
    customer: { id: string; name: string; code: string; tin: string | null };
  };
}

interface ParameterGroup {
  key: string;
  values: { id: string; label: string }[];
}

export function BillingDetailPage(): JSX.Element {
  const { id } = useParams();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [payment, setPayment] = useState({
    dateReceived: new Date().toISOString().slice(0, 10),
    cashAmount: '',
    ewtAmount: '',
    wvatAmount: '',
    paymentModeId: '',
    referenceNo: '',
    orInvoiceNo: '',
    form2307Received: false,
    form2306Received: false,
    remarks: '',
  });

  const billing = useQuery({
    queryKey: ['billing', id],
    queryFn: () => api<{ data: BillingDetail }>(`/billings/${id}`),
  });
  const parameters = useQuery({
    queryKey: ['parameters'],
    queryFn: () => api<{ data: ParameterGroup[] }>('/parameters'),
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['billing', id] });
    void queryClient.invalidateQueries({ queryKey: ['billings'] });
    void queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
  };

  const issue = useMutation({
    mutationFn: () => api(`/billings/${id}/issue`, { method: 'POST' }),
    onSuccess: () => {
      setError(null);
      setNotice('Statement issued and downloading. Its amounts are now fixed.');
      refresh();
      // Issuing exists to send the statement out, so hand over the PDF at once
      // rather than making someone find the download button afterwards.
      void openPdf(false);
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not issue the statement.'),
  });

  const voidBilling = useMutation({
    mutationFn: () => api(`/billings/${id}/void`, { method: 'POST', body: { reason: voidReason } }),
    onSuccess: () => {
      setError(null);
      setVoiding(false);
      setNotice('Statement voided.');
      refresh();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not void the statement.'),
  });

  const recordPayment = useMutation({
    mutationFn: () =>
      api('/billings/collections', {
        method: 'POST',
        body: {
          billingId: id,
          dateReceived: payment.dateReceived,
          cashAmount: Number(payment.cashAmount || 0),
          ewtAmount: Number(payment.ewtAmount || 0),
          wvatAmount: Number(payment.wvatAmount || 0),
          paymentModeId: payment.paymentModeId || undefined,
          referenceNo: payment.referenceNo || undefined,
          orInvoiceNo: payment.orInvoiceNo || undefined,
          form2307Received: payment.form2307Received,
          form2306Received: payment.form2306Received,
          remarks: payment.remarks || undefined,
        },
      }),
    onSuccess: () => {
      setError(null);
      setPaying(false);
      setNotice('Payment recorded. Finance must confirm it before the receivable moves.');
      refresh();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not record the payment.'),
  });

  const decide = useMutation({
    mutationFn: (input: { collectionId: string; decision: 'CONFIRMED' | 'REJECTED'; remarks?: string }) =>
      api(`/billings/collections/${input.collectionId}/decide`, {
        method: 'POST',
        body: { decision: input.decision, remarks: input.remarks },
      }),
    onSuccess: () => {
      setError(null);
      setNotice('Payment decided.');
      refresh();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not decide the payment.'),
  });

  const openPdf = async (inline: boolean): Promise<void> => {
    const response = await fetch(`/api/v1/billings/${id}/pdf?inline=${String(inline)}`, {
      credentials: 'include',
      headers: { authorization: `Bearer ${getAccessToken() ?? ''}` },
    });
    if (!response.ok) {
      setError('Could not produce the PDF.');
      return;
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    if (inline) {
      window.open(url, '_blank', 'noopener');
    } else {
      const link = document.createElement('a');
      link.href = url;
      link.download = `${billing.data?.data.code ?? 'statement'}.pdf`;
      link.click();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  if (billing.isLoading) return <Skeleton className="h-96 w-full" />;
  const row = billing.data?.data;
  if (!row) return <Alert tone="critical">Statement not found.</Alert>;

  const paymentModes = parameters.data?.data.find((g) => g.key === 'payment_mode')?.values ?? [];
  const outstanding = Number(row.total) - Number(row.settledAmount);
  const noVat = Number(row.vat) === 0;

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-mono text-xl font-bold text-navy dark:text-white">{row.code}</h1>
            <StatusChip tone={BILLING_TONES[row.status]}>{BILLING_LABELS[row.status]}</StatusChip>
            {row.salesOrder.isGovernment && <StatusChip tone="info">Government</StatusChip>}
          </div>
          <p className="text-sm text-slate-500">
            <Link to={`/customers/${row.salesOrder.customer.id}`} className="hover:underline">
              {row.salesOrder.customer.name}
            </Link>{' '}
            ·{' '}
            <Link to={`/sales-orders/${row.salesOrder.id}`} className="hover:underline">
              {row.salesOrder.code}
            </Link>{' '}
            · {row.salesOrder.projectName}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => void openPdf(true)}>
            <Eye aria-hidden="true" className="h-4 w-4" /> Preview
          </Button>
          <Button variant="secondary" onClick={() => void openPdf(false)}>
            <Download aria-hidden="true" className="h-4 w-4" /> Download
          </Button>
          {row.status === 'DRAFT' && can('billing.issue') && (
            <Button busy={issue.isPending} onClick={() => issue.mutate()}>
              <Check aria-hidden="true" className="h-4 w-4" /> Issue
            </Button>
          )}
          {row.status !== 'VOID' && row.status !== 'PAID' && can('billing.void') && (
            <Button variant="secondary" onClick={() => setVoiding(true)}>
              <Ban aria-hidden="true" className="h-4 w-4" /> Void
            </Button>
          )}
        </div>
      </header>

      {notice && <Alert tone="ok">{notice}</Alert>}
      {error && <Alert tone="critical">{error}</Alert>}
      {row.voidReason && <Alert tone="warning">Voided: {row.voidReason}</Alert>}

      <div className="grid gap-5 lg:grid-cols-3">
        <Card title="Amounts" className="lg:col-span-2">
          <TableWrap>
            <thead>
              <tr>
                <Th>Description</Th>
                <Th align="right">{noVat ? 'Amount' : 'Amount (VAT-ex)'}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {row.lines.map((line) => (
                <tr key={line.id}>
                  <Td>{line.description}</Td>
                  <Td align="right" className="tabular">
                    {peso(line.amountVatEx)}
                  </Td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t-2 border-slate-200 dark:border-slate-700">
              {/* A bill without VAT shows only the total (boss, 22 Oct 2026). */}
              {!noVat && (
                <>
                  <tr>
                    <Td align="right">Subtotal</Td>
                    <Td align="right" className="tabular">
                      {peso(row.amountVatEx)}
                    </Td>
                  </tr>
                  <tr>
                    <Td align="right">VAT</Td>
                    <Td align="right" className="tabular">
                      {peso(row.vat)}
                    </Td>
                  </tr>
                </>
              )}
              <tr className="font-bold">
                <Td align="right">Total due</Td>
                <Td align="right" className="tabular">
                  {peso(row.total)}
                </Td>
              </tr>
            </tfoot>
          </TableWrap>
        </Card>

        <Card title="Position">
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Statement date</dt>
              <dd>{shortDate(row.billingDate)}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Due date</dt>
              <dd
                className={
                  row.status !== 'PAID' && new Date(row.dueDate) < new Date()
                    ? 'font-semibold text-red-600'
                    : ''
                }
              >
                {shortDate(row.dueDate)}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Settled</dt>
              <dd className="tabular">{peso(row.settledAmount)}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Outstanding</dt>
              <dd className="tabular text-lg font-bold text-navy dark:text-white">
                {peso(outstanding)}
              </dd>
            </div>
          </dl>
        </Card>
      </div>

      <Card
        title="Payments"
        description="Cash plus any tax the customer withheld. Only a confirmed payment moves the receivable."
        actions={
          row.status !== 'DRAFT' &&
          row.status !== 'VOID' &&
          can('collections.create') && (
            <Button size="sm" onClick={() => setPaying(true)}>
              <Plus aria-hidden="true" className="h-4 w-4" /> Record payment
            </Button>
          )
        }
      >
        {row.collections.length === 0 ? (
          <EmptyState title="No payments recorded yet" />
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <Th>Received</Th>
                <Th align="right">Cash</Th>
                <Th align="right">EWT</Th>
                <Th align="right">WVAT</Th>
                <Th align="right">Settles</Th>
                <Th>Reference</Th>
                <Th>Status</Th>
                {can('collections.confirm') && <Th align="right">Decide</Th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {row.collections.map((collection) => (
                <tr key={collection.id}>
                  <Td>{shortDate(collection.dateReceived)}</Td>
                  <Td align="right" className="tabular">
                    {peso(collection.cashAmount)}
                  </Td>
                  <Td align="right" className="tabular">
                    {peso(collection.ewtAmount)}
                    {collection.form2307Received && (
                      <span className="ml-1 text-xs text-emerald-600">2307</span>
                    )}
                  </Td>
                  <Td align="right" className="tabular">
                    {peso(collection.wvatAmount)}
                    {collection.form2306Received && (
                      <span className="ml-1 text-xs text-emerald-600">2306</span>
                    )}
                  </Td>
                  <Td align="right" className="tabular font-semibold">
                    {peso(
                      Number(collection.cashAmount) +
                        Number(collection.ewtAmount) +
                        Number(collection.wvatAmount),
                    )}
                  </Td>
                  <Td>
                    <p>{collection.referenceNo ?? '—'}</p>
                    {collection.orInvoiceNo && (
                      <p className="text-xs text-slate-500">OR {collection.orInvoiceNo}</p>
                    )}
                  </Td>
                  <Td>
                    <StatusChip
                      tone={
                        collection.status === 'CONFIRMED'
                          ? 'ok'
                          : collection.status === 'REJECTED'
                            ? 'critical'
                            : 'warning'
                      }
                    >
                      {collection.status === 'PENDING'
                        ? 'Pending confirmation'
                        : collection.status.charAt(0) + collection.status.slice(1).toLowerCase()}
                    </StatusChip>
                  </Td>
                  {can('collections.confirm') && (
                    <Td align="right">
                      {collection.status === 'PENDING' && (
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            onClick={() =>
                              decide.mutate({ collectionId: collection.id, decision: 'CONFIRMED' })
                            }
                          >
                            <Check aria-hidden="true" className="h-3.5 w-3.5" /> Confirm
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                              const remarks = window.prompt('Why is this payment rejected?');
                              if (remarks)
                                decide.mutate({
                                  collectionId: collection.id,
                                  decision: 'REJECTED',
                                  remarks,
                                });
                            }}
                          >
                            <X aria-hidden="true" className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </Td>
                  )}
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Card>

      {paying && (
        <Modal
          title="Record a payment"
          description={`${peso(outstanding)} is outstanding on this statement.`}
          onClose={() => setPaying(false)}
        >
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              recordPayment.mutate();
            }}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Date received" required>
                <input
                  className={inputClass}
                  type="date"
                  required
                  value={payment.dateReceived}
                  onChange={(event) =>
                    setPayment({ ...payment, dateReceived: event.target.value })
                  }
                />
              </Field>
              <Field label="Payment mode">
                <select
                  className={inputClass}
                  value={payment.paymentModeId}
                  onChange={(event) =>
                    setPayment({ ...payment, paymentModeId: event.target.value })
                  }
                >
                  <option value="">Not set</option>
                  {paymentModes.map((mode) => (
                    <option key={mode.id} value={mode.id}>
                      {mode.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Cash received" required>
                <input
                  className={`${inputClass} tabular`}
                  type="number"
                  step="0.01"
                  min={0}
                  required
                  value={payment.cashAmount}
                  onChange={(event) => setPayment({ ...payment, cashAmount: event.target.value })}
                />
              </Field>
              <Field label="Withholding tax (EWT)" hint="What the customer kept and will remit.">
                <input
                  className={`${inputClass} tabular`}
                  type="number"
                  step="0.01"
                  min={0}
                  value={payment.ewtAmount}
                  onChange={(event) => setPayment({ ...payment, ewtAmount: event.target.value })}
                />
              </Field>
              {row.salesOrder.isGovernment && (
                <Field label="Withholding VAT">
                  <input
                    className={`${inputClass} tabular`}
                    type="number"
                    step="0.01"
                    min={0}
                    value={payment.wvatAmount}
                    onChange={(event) => setPayment({ ...payment, wvatAmount: event.target.value })}
                  />
                </Field>
              )}
              <Field label="Reference number" hint="Cheque or transfer reference.">
                <input
                  className={inputClass}
                  value={payment.referenceNo}
                  onChange={(event) => setPayment({ ...payment, referenceNo: event.target.value })}
                />
              </Field>
              <Field label="Official receipt / invoice no.">
                <input
                  className={inputClass}
                  value={payment.orInvoiceNo}
                  onChange={(event) => setPayment({ ...payment, orInvoiceNo: event.target.value })}
                />
              </Field>
            </div>

            <div className="flex flex-wrap gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-navy focus:ring-brand"
                  checked={payment.form2307Received}
                  onChange={(event) =>
                    setPayment({ ...payment, form2307Received: event.target.checked })
                  }
                />
                BIR 2307 received
              </label>
              {row.salesOrder.isGovernment && (
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-navy focus:ring-brand"
                    checked={payment.form2306Received}
                    onChange={(event) =>
                      setPayment({ ...payment, form2306Received: event.target.checked })
                    }
                  />
                  BIR 2306 received
                </label>
              )}
            </div>

            <div className="rounded-xl bg-slate-50 p-3 text-sm dark:bg-slate-800">
              This payment settles{' '}
              <strong className="tabular">
                {peso(
                  Number(payment.cashAmount || 0) +
                    Number(payment.ewtAmount || 0) +
                    Number(payment.wvatAmount || 0),
                )}
              </strong>{' '}
              of {peso(outstanding)}.
            </div>

            <Field label="Remarks">
              <input
                className={inputClass}
                value={payment.remarks}
                onChange={(event) => setPayment({ ...payment, remarks: event.target.value })}
              />
            </Field>

            <div className="flex gap-2">
              <Button type="submit" busy={recordPayment.isPending}>
                Record payment
              </Button>
              <Button type="button" variant="secondary" onClick={() => setPaying(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {voiding && (
        <Modal title={`Void ${row.code}?`} onClose={() => setVoiding(false)}>
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              voidBilling.mutate();
            }}
          >
            <Alert tone="warning">
              The statement stays on file, marked void, and anything billed by deliverable becomes
              billable again.
            </Alert>
            <Field label="Reason" required>
              <textarea
                className={`${inputClass} h-24`}
                required
                minLength={5}
                value={voidReason}
                onChange={(event) => setVoidReason(event.target.value)}
              />
            </Field>
            <div className="flex gap-2">
              <Button type="submit" variant="danger" busy={voidBilling.isPending}>
                Void the statement
              </Button>
              <Button type="button" variant="secondary" onClick={() => setVoiding(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
